import { useCallback, useEffect, useRef, useState } from 'react'
import {
  AssetBreakdown,
  AssetBreakdownDimension,
  AssetFilter,
  AssetStatistics,
  AssetTrendPoint,
  LastSeenWindow,
  TrendPeriod
} from 'models'
import { AssetInventoryServiceV4 } from 'services'
import { useAppDispatch } from 'store/hooks'
import { closeToast, openToast, ToastModel, ToastVariant } from 'store/toasts/toastSlice'
import { AssetTabConfig, DEFAULT_TREND_PERIOD, EMPTY_COUNT, EMPTY_STATISTICS } from '../assetInventory.constants'

const EMPTY_BREAKDOWN: AssetBreakdown = { available: false, buckets: [], lastUpdatedAt: null }
const TOAST_DURATION_MS = 5000

interface Props {
  config: AssetTabConfig
  lastSeen: LastSeenWindow
  active: boolean
}

export const useAssetTabData = ({ config, lastSeen, active }: Props) => {
  const { assetType, breakdownDimensions } = config

  const [statistics, setStatistics] = useState<AssetStatistics | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)

  const [trend, setTrend] = useState<AssetTrendPoint[]>([])
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>(DEFAULT_TREND_PERIOD)
  const [trendLoading, setTrendLoading] = useState(true)

  const [dimension, setDimension] = useState<AssetBreakdownDimension>(breakdownDimensions[0])
  const [breakdown, setBreakdown] = useState<AssetBreakdown>(EMPTY_BREAKDOWN)
  const [breakdownLoading, setBreakdownLoading] = useState(true)

  const [filters, setFilters] = useState<AssetFilter[]>([])
  const [filtersWindow, setFiltersWindow] = useState(lastSeen)

  /* Reset during render so the grid never sees the new window with the old shortcuts and fetches twice. */
  if (filtersWindow !== lastSeen) {
    setFiltersWindow(lastSeen)
    setFilters([])
  }
  const dispatch = useAppDispatch()
  const toastTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(toastTimer.current), [])

  /* Every window comes back together, so the global control is read out of this, not re-asked. */
  useEffect(() => {
    if (!active) return

    let cancelled = false
    setStatsLoading(true)

    AssetInventoryServiceV4.getStatistics(assetType)
      .then((next) => !cancelled && setStatistics(next))
      .catch((err) => {
        console.error(err)
        if (!cancelled) setStatistics(EMPTY_STATISTICS)
      })
      .finally(() => !cancelled && setStatsLoading(false))

    return () => {
      cancelled = true
    }
  }, [assetType, active])

  const count = statistics ? statistics.windows[lastSeen] ?? EMPTY_COUNT : null
  const recentlyAdded = statistics?.recentlyAdded ?? null

  useEffect(() => {
    if (!active) return

    let cancelled = false
    setTrendLoading(true)

    AssetInventoryServiceV4.getTrend(assetType, lastSeen, trendPeriod)
      .then((points) => !cancelled && setTrend(points))
      .catch((err) => console.error(err))
      .finally(() => !cancelled && setTrendLoading(false))

    return () => {
      cancelled = true
    }
  }, [assetType, lastSeen, trendPeriod, active])

  useEffect(() => {
    if (!active) return

    let cancelled = false
    setBreakdownLoading(true)

    AssetInventoryServiceV4.getBreakdown(assetType, lastSeen, dimension)
      .then((next) => !cancelled && setBreakdown(next))
      .catch((err) => {
        console.error(err)
        if (!cancelled) setBreakdown(EMPTY_BREAKDOWN)
      })
      .finally(() => !cancelled && setBreakdownLoading(false))

    return () => {
      cancelled = true
    }
  }, [assetType, lastSeen, dimension, active])

  /* A shortcut replaces the one before it on the same statistic. */
  const applyFilter = useCallback(
    (filter: AssetFilter) => {
      setFilters((current) => [...current.filter((item) => item.id !== filter.id), filter])

      /* The grid can sit below the fold, so the click is confirmed where the user is looking. */
      dispatch(
        openToast({
          variant: ToastVariant.success,
          toastModel: ToastModel.Trends,
          props: { text: `${filter.label} filter applied: ${filter.value}`, description: '' }
        })
      )
      clearTimeout(toastTimer.current)
      toastTimer.current = setTimeout(() => dispatch(closeToast()), TOAST_DURATION_MS)
    },
    [dispatch]
  )

  const clearFilter = useCallback((id: string) => setFilters((current) => current.filter((item) => item.id !== id)), [])

  return {
    count,
    recentlyAdded,
    statsLoading,
    trend,
    trendPeriod,
    setTrendPeriod,
    trendLoading,
    dimension,
    setDimension,
    breakdown,
    breakdownLoading,
    filters,
    applyFilter,
    clearFilter
  }
}
